import { useState } from "react";

export type CreateAppointmentStep = "customer" | "services" | "scheduling";

interface UseStepNavigationProps {
  initialStep?: CreateAppointmentStep;
  canGoToCustomer?: () => boolean;
  canGoToServices?: () => boolean;
  canGoToScheduling?: () => boolean;
}

export function useStepNavigation({
  initialStep = "customer",
  canGoToCustomer = () => true,
  canGoToServices = () => false,
  canGoToScheduling = () => false,
}: UseStepNavigationProps = {}) {
  const [currentStep, setCurrentStep] = useState<CreateAppointmentStep>(initialStep);

  // Define step order
  const steps: CreateAppointmentStep[] = ["customer", "services", "scheduling"];

  // Check if can go to specific step
  const canGoToStep = (step: CreateAppointmentStep): boolean => {
    switch (step) {
      case "customer":
        return canGoToCustomer();
      case "services":
        return canGoToServices();
      case "scheduling":
        return canGoToScheduling();
      default:
        return false;
    }
  };

  // Go to specific step
  const goToStep = (step: CreateAppointmentStep) => {
    if (canGoToStep(step)) {
      setCurrentStep(step);
    }
  };

  // Go to next step
  const nextStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      const nextStepName = steps[currentIndex + 1];
      if (canGoToStep(nextStepName)) {
        setCurrentStep(nextStepName);
      }
    }
  };

  // Go to previous step
  const prevStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStepName = steps[currentIndex - 1];
      if (canGoToStep(prevStepName)) {
        setCurrentStep(prevStepName);
      }
    }
  };

  // Check if current step is first
  const isFirstStep = () => {
    return currentStep === steps[0];
  };

  // Check if current step is last
  const isLastStep = () => {
    return currentStep === steps[steps.length - 1];
  };

  // Check if can go to next step
  const canGoNext = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      const nextStepName = steps[currentIndex + 1];
      return canGoToStep(nextStepName);
    }
    return false;
  };

  // Check if can go to previous step
  const canGoPrev = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStepName = steps[currentIndex - 1];
      return canGoToStep(prevStepName);
    }
    return false;
  };

  // Get step index
  const getStepIndex = (step: CreateAppointmentStep): number => {
    return steps.indexOf(step);
  };

  // Check if step is completed (step index < current step index)
  const isStepCompleted = (step: CreateAppointmentStep): boolean => {
    return getStepIndex(step) < getStepIndex(currentStep);
  };

  // Reset to initial step
  const reset = () => {
    setCurrentStep(initialStep);
  };

  return {
    // Current state
    currentStep,
    setCurrentStep,
    steps,

    // Navigation
    goToStep,
    nextStep,
    prevStep,
    canGoToStep,
    canGoNext,
    canGoPrev,

    // Step info
    isFirstStep,
    isLastStep,
    getStepIndex,
    isStepCompleted,

    // Actions
    reset,
  };
}
